// src/components/BossQuestCard.js
import React, { useEffect, useMemo, useState } from "react";
import { FiXCircle } from "react-icons/fi";

import {
  useBossQuest as useBossQuestHook,
  autoGenerateBossQuestIfNeeded,
} from "../hooks/useBossQuest";

import {
  getWeekId,
  getBossQuestPresentation,
} from "../utils/bossQuestUtils";
import { orchestrateBossQuest } from "../api/systemApi";
import {
  getProfile,
  subscribeToProfile,
  upsertBossQuest,
  upsertProfile,
} from "../api/supabaseData";
import { playSystemSound } from "../audio/soundManager";

const BossQuestCard = ({ user, onProfileUpdate }) => {
  const uid = user?.uid;

  // Profile (level + auto toggle)
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!uid) return;
    let active = true;

    getProfile(uid)
      .then((row) => {
        if (active) {
          setProfile(row);
        }
      })
      .catch((error) => console.error("Failed to load boss profile:", error));

    const unsubscribe = subscribeToProfile(uid, (nextProfile) => {
      if (active) {
        setProfile(nextProfile);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [uid]);

  const level = Number(profile?.level || 1);
  const autoBoss = !!profile?.autoBossQuest;

  // Hook (supports either new or legacy return shape)
  const h =
    useBossQuestHook(uid, {
      onProfileUpdate,
      timeZone: profile?.timeZone || null,
    }) || {};
  const bossQuest = h.bossQuest || null;
  const claimBossQuest = h.claimBossQuest;
  const createOrUpdateBossQuest = h.createOrUpdateBossQuest || h.addOrEditBossQuest;
  const bossQuestView = useMemo(
    () => (bossQuest ? getBossQuestPresentation(bossQuest) : null),
    [bossQuest]
  );

  // UI state
  const [statusMsg, setStatusMsg] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [previousBossStatus, setPreviousBossStatus] = useState(null);

  // Goal modal (AI path when no last-week data)
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [bossGoal, setBossGoal] = useState("");

  // Manual add/edit modal
  const [showManualModal, setShowManualModal] = useState(false);
  const [form, setForm] = useState({
    title: "",
    desc: "",
    xp: 500,
  });

  // Auto-generate on mount (if enabled)
  useEffect(() => {
    if (!uid || !autoBoss) return;
    autoGenerateBossQuestIfNeeded(uid, level);
  }, [uid, autoBoss, level]);

  useEffect(() => {
    if (!bossQuest?.id && !bossQuest?.title) return;

    const nextStatus = bossQuest?.status || "pending";
    if (previousBossStatus == null) {
      setPreviousBossStatus(nextStatus);
      return;
    }

    if (previousBossStatus !== "completed" && nextStatus === "completed") {
      playSystemSound("bossClaim");
    }

    setPreviousBossStatus(nextStatus);
  }, [bossQuest, previousBossStatus]);

  // ---- Handlers
  const handleToggleAuto = async (e) => {
    if (!uid) return;
    const checked = e.target.checked;
    setStatusMsg("");
    try {
      await upsertProfile(uid, { autoBossQuest: checked });
      setStatusMsg(
        checked
          ? "Boss quest will auto-generate every Monday 12:00 AM."
          : "Auto-generation disabled."
      );
    } catch (err) {
      console.error(err);
      setStatusMsg("Could not save preference.");
    }
  };

  const handleGenerateNow = async () => {
    if (!uid) return;
    setIsBusy(true);
    setStatusMsg("");
    try {
      const result = await orchestrateBossQuest({
        mode: "history",
        userLevel: level,
      });
      if (result.status === "no-data") {
        playSystemSound("questOpen");
        setShowGoalModal(true); // No last-week data → ask for goal
        return;
      }
      if (!result.bossQuest) {
        setStatusMsg(result.message || "No boss quest could be generated.");
        return;
      }
      setStatusMsg("Boss quest generated for this week.");
      playSystemSound("bossReady");
    } catch (err) {
      console.error(err);
      setStatusMsg("Failed to generate boss quest.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleGenerateFromGoal = async () => {
    if (!uid) return;
    const goal = bossGoal.trim();
    if (!goal) return;
    setIsBusy(true);
    setStatusMsg("");
    try {
      const result = await orchestrateBossQuest({
        mode: "goal",
        goal,
        userLevel: level,
      });
      if (!result.bossQuest) {
        setStatusMsg(result.message || "Failed to generate from goal.");
        return;
      }
      setShowGoalModal(false);
      setBossGoal("");
      setStatusMsg("Boss quest generated for this week.");
      playSystemSound("bossReady");
    } catch (err) {
      console.error(err);
      setStatusMsg("Failed to generate from goal.");
    } finally {
      setIsBusy(false);
    }
  };

  // Manual add
  const openManual = () => {
    setForm({
      title: bossQuest?.title || "",
      desc: bossQuest?.desc || "",
      xp: Number(bossQuest?.xp || 500),
    });
    playSystemSound("questOpen");
    setShowManualModal(true);
  };

  const handleManualSave = async () => {
    if (!uid) return;
    if (!form.title || !form.xp) return;
    setIsBusy(true);
    try {
      await writeBossQuest(uid, {
        title: String(form.title),
        desc: String(form.desc || ""),
        xp: Number(form.xp) || 0,
        source: "manual",
      });
      setShowManualModal(false);
      setStatusMsg("Boss quest saved.");
      playSystemSound("bossReady");
    } catch (err) {
      console.error(err);
      setStatusMsg("Failed to save boss quest.");
    } finally {
      setIsBusy(false);
    }
  };

  // ---- Helpers
  async function writeBossQuest(uid, quest) {
    const weekId = getWeekId(new Date(), profile?.timeZone || null);
    if (createOrUpdateBossQuest) {
      await createOrUpdateBossQuest({ title: quest.title, desc: quest.desc, xp: quest.xp });
      return;
    }
    await upsertBossQuest(uid, weekId, {
      ...quest,
      status: "pending",
      claimed: false,
      source: quest.source || "ai",
      createdAt: new Date().toISOString(),
    });
  }

  const header = useMemo(() => <h4>Boss Quest</h4>, []);

  return (
    // Use SAME card class as other panels; consistent size
    <div className="quests-card boss-card">
      {header}

      <div className="boss-panel">
        <div className="boss-controls">
          <label className="boss-toggle-card">
            <div className="boss-toggle-copy">
              <span className="boss-toggle-title">Auto-add boss quest</span>
              <span className="boss-toggle-subtitle">Every Monday at 12:00 AM</span>
            </div>
            <input type="checkbox" checked={autoBoss} onChange={handleToggleAuto} />
          </label>

          <div className="boss-action-grid">
            <button
              className="login-btn boss-action-btn"
              type="button"
              onClick={handleGenerateNow}
              disabled={isBusy}
            >
              {isBusy ? "Generating…" : "Generate Boss Quest"}
            </button>

            <button className="login-btn boss-action-btn" type="button" onClick={openManual}>
              + Add Manually
            </button>
          </div>
        </div>

        <div className="boss-content-card">
          {bossQuest ? (
            <>
              <div className="boss-title">{bossQuest.title}</div>
              <div className="boss-desc">{bossQuestView?.summary || bossQuest.desc}</div>

              {bossQuestView?.checklist?.length ? (
                <div className="boss-checklist-card">
                  <div className="boss-checklist-title">What to do this week</div>
                  <ul className="boss-checklist">
                    {bossQuestView.checklist.map((item, index) => (
                      <li key={`${bossQuest.title}-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {bossQuestView?.acceptance ? (
                <div className="boss-acceptance">
                  <span className="boss-acceptance__label">Complete when:</span>
                  <span className="boss-acceptance__text">{bossQuestView.acceptance}</span>
                </div>
              ) : null}

              <div className="boss-meta">
                <div className="boss-meta-chip">
                  <span className="boss-meta-label">XP</span>
                  <span className="boss-meta-value">{Number(bossQuest.xp || 0)}</span>
                </div>

                <div className="boss-meta-chip">
                  <span className="boss-meta-label">Status</span>
                  {bossQuest.status === "completed" ? (
                    <span className="boss-meta-value ok">Completed</span>
                  ) : (
                    <span className="boss-meta-value pending">Pending</span>
                  )}
                </div>
              </div>

              {bossQuest.status !== "completed" && (
                <div className="boss-claim-row">
                  <button
                    className="login-btn boss-action-btn boss-action-btn--claim"
                    type="button"
                    onClick={() => claimBossQuest && claimBossQuest()}
                  >
                    Claim XP
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="boss-empty">No boss quest generated for this week yet.</div>
          )}
        </div>

        {statusMsg && <div className="boss-status">{statusMsg}</div>}
      </div>

      {/* ===== AI Goal Modal (no last-week data) ===== */}
      {showGoalModal && (
        <div className="solo-modal-bg" onClick={() => setShowGoalModal(false)}>
          <form
            className="solo-modal boss-quest-modal"
            onSubmit={(e) => {
              e.preventDefault();
              handleGenerateFromGoal();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="solo-close"
              type="button"
              onClick={() => setShowGoalModal(false)}
              title="Close"
            >
              <FiXCircle />
            </button>

            <div className="boss-quest-modal__header">
              <h2>Generate Boss Quest</h2>
              <p className="helper boss-quest-modal__helper">
                We couldn&apos;t find enough data from last week. Set one clear goal for this
                week and the system will build a boss quest around it.
              </p>
            </div>

            <div className="solo-field">
              <label htmlFor="bossGoal">Primary goal for this week</label>
              <textarea
                id="bossGoal"
                rows={3}
                value={bossGoal}
                onChange={(e) => setBossGoal(e.target.value)}
                placeholder="e.g., Run 20km total this week, or finish module 2 of my course"
                maxLength={180}
              />
            </div>

            <div className="solo-actions boss-quest-modal__actions">
              <button
                className="logout-btn boss-quest-modal__action-btn"
                type="button"
                onClick={() => setShowGoalModal(false)}
              >
                Cancel
              </button>
              <button
                className="login-btn boss-quest-modal__action-btn"
                type="submit"
                disabled={isBusy || !bossGoal.trim()}
              >
                {isBusy ? "Generating…" : "Generate"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ===== Manual Add/Edit Modal ===== */}
      {showManualModal && (
        <div className="solo-modal-bg" onClick={() => setShowManualModal(false)}>
          <form
            className="solo-modal boss-quest-modal boss-quest-modal--manual"
            onSubmit={(e) => {
              e.preventDefault();
              handleManualSave();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="solo-close"
              type="button"
              onClick={() => setShowManualModal(false)}
              title="Close"
            >
              <FiXCircle />
            </button>

            <div className="boss-quest-modal__header">
              <h2>{bossQuest ? "Edit Boss Quest" : "Add Boss Quest"}</h2>
              <p className="helper boss-quest-modal__helper">
                Set the weekly title, objective, and XP reward manually.
              </p>
            </div>

            <div className="solo-field">
              <label htmlFor="bossQuestTitle">Title</label>
              <input
                id="bossQuestTitle"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Conquer the 10km mountain trail"
                maxLength={60}
              />
            </div>

            <div className="solo-field">
              <label htmlFor="bossQuestDesc">Description</label>
              <input
                id="bossQuestDesc"
                value={form.desc}
                onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))}
                placeholder="Short details or acceptance criteria"
                maxLength={140}
              />
            </div>

            <div className="solo-field boss-quest-modal__xp">
              <label htmlFor="bossQuestXp">XP</label>
              <input
                id="bossQuestXp"
                type="number"
                min={100}
                step={10}
                value={form.xp}
                onChange={(e) => setForm((f) => ({ ...f, xp: Number(e.target.value) }))}
              />
            </div>

            <div className="solo-actions boss-quest-modal__actions">
              <button
                className="logout-btn boss-quest-modal__action-btn"
                type="button"
                onClick={() => setShowManualModal(false)}
              >
                Cancel
              </button>
              <button
                className="login-btn boss-quest-modal__action-btn"
                type="submit"
                disabled={isBusy}
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default BossQuestCard;
